/** SaaS project REST client — calls `/projects` only. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateProjectDto, UpdateProjectDto, Project, ProjectType } from '@cms/models';
import apiClient from './axios';

export interface ProjectListFilters {
  type?: ProjectType;
  nameContains?: string;
}

const BASE = '/projects';

export const projectsQueryKeys = {
  all: ['projects'] as const,
  list: (filters?: ProjectListFilters) => ['projects', 'list', filters ?? {}] as const,
};

function buildListParams(filters?: ProjectListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.type) params.type = filters.type;
  if (filters?.nameContains?.trim()) params.name_contains = filters.nameContains.trim();
  return params;
}

export const projectsApi = {
  async list(filters?: ProjectListFilters): Promise<Project[]> {
    const params = buildListParams(filters);
    const { data } = await apiClient.get<Project[]>(BASE, { params });
    return Array.isArray(data) ? data : [];
  },
  async getById(id: string): Promise<Project> {
    const { data } = await apiClient.get<Project>(`${BASE}/${id}`);
    return data;
  },
  async create(payload: CreateProjectDto): Promise<Project> {
    const { data } = await apiClient.post<Project>(BASE, payload);
    return data;
  },
  async update(id: string, payload: UpdateProjectDto): Promise<Project> {
    const { data } = await apiClient.patch<Project>(`${BASE}/${id}`, payload);
    return data;
  },
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
  /** Server-side fetch of public Google Sheet CSV (avoids browser CORS). */
  async fetchGoogleSheetCsv(sheetUrl: string): Promise<string> {
    const { data } = await apiClient.get<{ csv: string }>(`${BASE}/import/google-sheet`, {
      params: { url: sheetUrl },
    });
    if (!data || typeof data.csv !== 'string') {
      throw new Error('Invalid response when importing Google Sheet.');
    }
    return data.csv;
  },
};

export function useProjects(filters?: ProjectListFilters) {
  return useQuery({
    queryKey: projectsQueryKeys.list(filters),
    queryFn: () => projectsApi.list(filters),
  });
}

export function useProject(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...projectsQueryKeys.all, 'detail', id] as const,
    queryFn: () => projectsApi.getById(id!),
    enabled: Boolean(enabled && id && id !== 'new'),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProjectDto) => projectsApi.create(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectsQueryKeys.all });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; payload: UpdateProjectDto }) =>
      projectsApi.update(params.id, params.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectsQueryKeys.all });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectsQueryKeys.all });
    },
  });
}
